import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../lib/api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'barber';
  commission_percent?: number;
}

export interface AuthSalon {
  id: string;
  slug: string;
  name: string;
  plan_id?: string;
  plan_status?: string;
  timezone?: string;
}

export interface SignupInput {
  email: string;
  password: string;
  name: string;
  salon_name: string;
  slug?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

interface AuthContextValue {
  ready: boolean;
  token: string | null;
  user: AuthUser | null;
  salon: AuthSalon | null;
  signup: (input: SignupInput) => Promise<{ ok: true } | { ok: false; error: string }>;
  login: (input: LoginInput) => Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'tijera.token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [salon, setSalon] = useState<AuthSalon | null>(null);
  const [ready, setReady] = useState(false);

  // Persistencia del token.
  useEffect(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, [token]);

  // Hidratacion: si hay token al arrancar, traer /me.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setReady(true);
      return;
    }

    (async () => {
      const res = await api<{ ok: true; user: AuthUser; salon: AuthSalon }>('/api/auth/me', { token });
      if (cancelled) return;
      if (res.ok) {
        setUser(res.data.user);
        setSalon(res.data.salon);
        if (res.refreshedToken) setToken(res.refreshedToken);
      } else {
        // Token invalido/expirado: limpiar.
        setToken(null);
        setUser(null);
        setSalon(null);
      }
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const signup = useCallback(async (input: SignupInput) => {
    const res = await api<{ ok: true; token: string; user: AuthUser; salon: AuthSalon }>(
      '/api/auth/signup',
      { method: 'POST', body: input },
    );
    if (!res.ok) return { ok: false as const, error: res.error };
    setToken(res.data.token);
    setUser(res.data.user);
    setSalon(res.data.salon);
    return { ok: true as const };
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const res = await api<{ ok: true; token: string; user: AuthUser; salon: AuthSalon }>(
      '/api/auth/login',
      { method: 'POST', body: input },
    );
    if (!res.ok) return { ok: false as const, error: res.error };
    setToken(res.data.token);
    setUser(res.data.user);
    setSalon(res.data.salon);
    return { ok: true as const };
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setSalon(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ready, token, user, salon, signup, login, logout }),
    [ready, token, user, salon, signup, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
